import { canLoadHeydayPage, canPerformHeydayAction, heydayPageLabel, heydayPageManifest, type HeydayCapabilityContext, type HeydayPage } from '../../config/heydayPageManifest';
import { heydaySearchActions, heydaySearchPages } from '../../config/heydaySearch';
import { buildEmployeeDirectoryIndex, employeeDirectoryMemberships, normalizeDirectoryQuery, unassignedOrganization, type EmployeeDirectorySnapshot } from '../../features/people/public';

export type SearchCategory = 'tasks' | 'people' | 'organizations';
export type SearchMode = 'employee' | 'admin';
export type SearchCapabilityContext = HeydayCapabilityContext & { tenantScopes?: readonly string[] };
export type SearchDestination =
  | { kind: 'page'; page: HeydayPage; query?: string }
  | { kind: 'worker'; workerId: string }
  | { kind: 'organization'; organizationId: string };

export type SearchCandidate = {
  id: string;
  category: SearchCategory;
  label: string;
  description: string;
  target: SearchDestination;
  page?: HeydayPage;
  mode?: SearchMode;
  avatarUrl?: string;
  searchTerms: readonly string[];
  suggestionOrder?: number;
};

export function normalizeSearchQuery(value: string) {
  return normalizeDirectoryQuery(value.normalize('NFKC')).replace(/\s+/g, ' ');
}

export function buildTaskCandidates({ visiblePages, capability }: {
  currentMode: SearchMode;
  visiblePages: ReadonlySet<HeydayPage>;
  capability: SearchCapabilityContext;
}): SearchCandidate[] {
  const canShowPage = (page: HeydayPage) => visiblePages.has(page) && canLoadHeydayPage(page, capability);
  const candidates: SearchCandidate[] = [];
  for (const [page, metadata] of Object.entries(heydaySearchPages) as Array<[HeydayPage, NonNullable<typeof heydaySearchPages[HeydayPage]>]>) {
    if (!canShowPage(page)) continue;
    const mode = heydayPageManifest[page].mode;
    candidates.push({
      id: `page:${page}`, category: 'tasks', page, mode,
      label: metadata.label ?? heydayPageLabel(page),
      description: mode === 'employee' ? '내 업무' : '관리자',
      target: { kind: 'page', page },
      searchTerms: [heydayPageLabel(page), ...metadata.aliases],
      suggestionOrder: metadata.suggestionOrder,
    });
  }
  for (const action of heydaySearchActions) {
    if (!canShowPage(action.page) || !canPerformHeydayAction(action.page, action.action, capability)) continue;
    // 구성원 등록은 특정 조직에 한정된 쓰기 권한으로 시작할 수 없다.
    if (action.page === 'people' && action.action === 'create' && !capability.fixtureMode
      && !heydayPageManifest.people.actions.create.allOf.every((scope) => capability.tenantScopes?.includes(scope))) continue;
    const mode = heydayPageManifest[action.page].mode;
    candidates.push({
      id: `action:${action.id}`, category: 'tasks', page: action.page, mode,
      label: action.label, description: mode === 'employee' ? '내 업무 · 신청' : '관리자 · 등록',
      target: { kind: 'page', page: action.page, query: action.query },
      searchTerms: action.aliases, suggestionOrder: action.suggestionOrder,
    });
  }
  return candidates;
}

type IndexedCandidate = {
  candidate: SearchCandidate;
  names: readonly string[];
  text: string;
};

function indexCandidate(candidate: SearchCandidate): IndexedCandidate {
  const terms = [candidate.label, ...candidate.searchTerms].map(normalizeSearchQuery);
  return { candidate, names: candidate.category === 'tasks' ? terms : terms.slice(0, 1), text: terms.join(' ') };
}

function matchingScore(candidate: IndexedCandidate, query: string, words: readonly string[]) {
  if (!words.every((word) => candidate.text.includes(word))) return undefined;
  if (candidate.names.some((name) => name === query)) return 0;
  if (candidate.names.some((name) => name.startsWith(query))) return 1;
  return 2;
}

function rankedMatches(candidates: readonly IndexedCandidate[], query: string, mode?: SearchMode) {
  const words = query.split(' ');
  return candidates.flatMap((candidate) => {
    const score = matchingScore(candidate, query, words);
    return score === undefined ? [] : [{ candidate: candidate.candidate, score }];
  }).sort((left, right) => left.score - right.score
    || (mode ? Number(right.candidate.mode === mode) - Number(left.candidate.mode === mode) : 0)
    || left.candidate.label.localeCompare(right.candidate.label, 'ko-KR')
    || left.candidate.id.localeCompare(right.candidate.id)).map(({ candidate }) => candidate);
}

export function searchTaskCandidates(candidates: readonly SearchCandidate[], query: string, { currentMode, limit }: { currentMode?: SearchMode; limit?: number } = {}): SearchCandidate[] {
  const normalized = normalizeSearchQuery(query);
  if (!normalized) return candidates.filter((candidate) => candidate.suggestionOrder !== undefined)
    .sort((left, right) => left.suggestionOrder! - right.suggestionOrder!)
    .slice(0, Math.min(limit ?? 3, 3));
  return rankedMatches(candidates.map(indexCandidate), normalized, currentMode).slice(0, limit);
}

export type DirectorySearchIndex = {
  people: readonly IndexedCandidate[];
  organizations: readonly IndexedCandidate[];
};

export function buildDirectorySearchIndex(snapshot: EmployeeDirectorySnapshot): DirectorySearchIndex {
  const directory = buildEmployeeDirectoryIndex(snapshot.organizations, snapshot.members);
  const paths = new Map(directory.groups.map((group) => [group.organization.id, group.path.map((organization) => organization.name).join(' › ')]));
  const knownOrganizationIds = new Set(snapshot.organizations.map((organization) => organization.id));
  const people = [...new Map(snapshot.members.map((member) => [member.id, member])).values()].map((member): SearchCandidate => {
    const memberships = employeeDirectoryMemberships(member);
    const organizationIds = [...new Set(memberships.map((membership) => membership.organizationId && knownOrganizationIds.has(membership.organizationId) ? membership.organizationId : unassignedOrganization.id))];
    const primaryMembership = memberships.find((membership) => membership.isPrimary) ?? memberships[0];
    const primaryId = primaryMembership?.organizationId && knownOrganizationIds.has(primaryMembership.organizationId)
      ? primaryMembership.organizationId : unassignedOrganization.id;
    const primaryPath = paths.get(primaryId) ?? unassignedOrganization.name;
    const additional = organizationIds.filter((id) => id !== primaryId).length;
    const organizationNames = organizationIds.map((id) => paths.get(id) ?? unassignedOrganization.name);
    return {
      id: `worker:${member.id}`, category: 'people', label: member.name,
      description: [member.title, primaryPath, additional ? `추가 소속 ${additional}개` : ''].filter(Boolean).join(' · '),
      avatarUrl: member.profilePhotoUrl,
      target: { kind: 'worker', workerId: member.id },
      searchTerms: [member.title, ...organizationNames],
    };
  }).map(indexCandidate);
  const organizations = directory.groups.map((group): SearchCandidate => ({
    id: `organization:${group.organization.id}`, category: 'organizations', label: group.organization.name,
    description: group.path.length > 1 ? group.path.slice(0, -1).map((organization) => organization.name).join(' › ') : group.organization.id === unassignedOrganization.id ? '소속을 확인할 구성원' : '최상위 조직',
    target: { kind: 'organization', organizationId: group.organization.id },
    searchTerms: [paths.get(group.organization.id) ?? group.organization.name],
  })).map(indexCandidate);
  return { people, organizations };
}

export function searchDirectoryCandidates(index: DirectorySearchIndex, query: string, { limitPerCategory }: { limitPerCategory?: number } = {}) {
  const normalized = normalizeSearchQuery(query);
  const people = normalized ? rankedMatches(index.people, normalized) : [];
  const organizations = normalized ? rankedMatches(index.organizations, normalized) : [];
  return {
    people: people.slice(0, limitPerCategory), organizations: organizations.slice(0, limitPerCategory),
    peopleTotal: people.length, organizationsTotal: organizations.length,
  };
}
